/* eslint-disable @typescript-eslint/no-explicit-any */
// src/app/services/b2c-claim.service.ts
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { BaseService } from './base.service';
import { ApiResp } from '@app/types/global.type';

export interface VerifyTin {
  idType: string;
  idValue: string;
  tin: string;
}
export interface InvoicePayload {
  supplierTin?: string;
  docNo?: string;
  docDate?: string;
  totalInvoiceValue?: number;
  items?: any[];
  sellerName?: string;
  addressLines?: string;
  city?: string;
  state?: string;
  country?: string;
  postal?: string;
}

export interface SubmitB2cClaimPayload {
  invoiceId: string;
  tin: string;
  submissionId: string;
}

@Injectable({
  providedIn: 'root',
})
export class B2cClaimService extends BaseService {
  private readonly SEARCH_EINVOICE_ENDPOINT = '/myportal/b2cClaim/search/einvoice';
  private readonly ADD_BUYER_ENDPOINT = '/myportal/b2cClaim/add/buyer';
  private readonly TOKEN_ENDPOINT = '/myportal/b2cClaim/token';
  private readonly GENERATE_EINVOICE_ENDPOINT = '/myportal/b2cClaim/generate/einvoice';
  private readonly SUBMIT_B2C_CLAIM_ENDPOINT = '/myportal/b2cClaim/submission';
  private readonly PRINT_PDF_ENDPOINT = '/myportal/einvoices/print/pdf';

  private readonly B2C_HEADER = {
    'b2c_claim_portal': 'true',
    'Accept': 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
  };

  private getHeaders(): { headers: HttpHeaders } {
    const headers = new HttpHeaders(this.B2C_HEADER as Record<string, string>);
    return { headers };
  }

  fetchInvoiceByQr(params: {
    supplierTin?: string;
    docNo?: string;
    docDate?: string;
    totalInvoiceValue?: string | number;
  }): Observable<ApiResp> {
    const body: Record<string, any> = {};

    if (params.supplierTin !== undefined && params.supplierTin !== null) {
      body.supplierTin = params.supplierTin;
    }
    if (params.docNo !== undefined && params.docNo !== null) {
      body.docNo = params.docNo;
    }
    if (params.docDate !== undefined && params.docDate !== null) {
      body.docDate = params.docDate;
    }
    if (params.totalInvoiceValue !== undefined && params.totalInvoiceValue !== null) {
      const n = Number(params.totalInvoiceValue);
      body.totalInvoiceValue = Number.isNaN(n) ? params.totalInvoiceValue : n;
    }

    return this.httpClient
      .post<ApiResp>(this.SEARCH_EINVOICE_ENDPOINT, body, this.getHeaders())
      .pipe(
        map((resp: ApiResp) => {
          if (resp?.status) return resp;
          this.toastr.error(resp?.message ?? 'Unable to fetch invoice');
          return resp;
        }),
        catchError((err) => {
          console.error('fetchInvoiceByQr error', err);
          this.toastr.error('Network error while fetching invoice');
          return of({ status: 0, message: 'Network error', response: null } as ApiResp);
        }),
      );
  }

  addBuyer(payload: any): Observable<ApiResp> {
    return this.httpClient.post<ApiResp>(this.ADD_BUYER_ENDPOINT, payload, this.getHeaders()).pipe(
      map((resp: ApiResp) => {
        try {
          return (this as any).handleFormResp ? (this as any).handleFormResp(resp) : resp;
        } catch {
          return resp;
        }
      }),
      catchError((err) => {
        console.error('addBuyer error', err);
        this.toastr.error('Failed to save buyer details');
        return of({
          status: 0,
          message: 'Failed to save buyer details',
          response: null,
        } as ApiResp);
      }),
    );
  }

  getToken(tin?: string): Observable<ApiResp> {
    let params = new HttpParams();
    if (tin) params = params.set('tin', tin);

    return this.httpClient.get<ApiResp>(this.TOKEN_ENDPOINT, { ...this.getHeaders(), params }).pipe(
      map((resp: ApiResp) => {
        if (resp?.status) return resp;
        this.toastr.error(resp?.message ?? 'Unable to fetch token');
        return resp;
      }),
      catchError((err) => {
        console.error('getToken error', err);
        this.toastr.error('Network error while fetching token');
        return of({ status: 0, message: 'Network error', response: null } as ApiResp);
      }),
    );
  }

  /**
   * generateEinvoice
   * Matches the curl you provided:
   * - Endpoint: /myportal/b2cClaim/generate/einvoice
   * - Sends JSON body and optional 'tin' as query param
   * - Adds the b2c_claim_portal header (via getHeaders)
   *
   * Example usage:
   * generateEinvoice({ tin: 'C987654321', docType: 'Invoice', invType: 'B2B', ... })
   */
  generateEinvoice(body: any): Observable<ApiResp> {
    // support passing body.tin or body.tinless (no tin)
    let params = new HttpParams();
    if (body?.tin) {
      params = params.set('tin', body.tin);
    }

    // remove tin from body payload if present (avoid duplication) — server accepts either but keep body clean
    const payload = { ...body };
    if (payload?.tin) delete payload.tin;

    return this.httpClient
      .post<ApiResp>(this.GENERATE_EINVOICE_ENDPOINT, payload, { ...this.getHeaders(), params })
      .pipe(
        map((resp: ApiResp) => {
          if (resp?.status) return resp;
          this.toastr.error(resp?.message ?? 'Unable to generate e-invoice');
          return resp;
        }),
        catchError((err) => {
          console.error('generateEinvoice error', err);
          this.toastr.error('Network error while generating e-invoice');
          return of({ status: 0, message: 'Network error', response: null } as ApiResp);
        }),
      );
  }

  updateEinvoice(
    id: string,
    payload: any,
    section: string,
    isEdit: boolean,
    token?: string | null,
  ): Observable<any> {
    try {
      const headersObj: Record<string, string> = {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',

        // merge B2C headers FIRST
        ...(this.B2C_HEADER as Record<string, string>),

        // add token LAST so nothing overrides it
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const headers = new HttpHeaders(headersObj);

      const url =
        `/myportal/update/einvoice/${encodeURIComponent(id)}` +
        `?section=${encodeURIComponent(section || '')}&isEdit=${isEdit}`;

      return this.httpClient.patch(url, payload, { headers }).pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((err) => {
          console.error('updateEinvoice error', err);
          this.toastr.error('Network error while updating e-invoice draft');
          return of({ status: 0, message: 'Network error', response: null } as any);
        }),
      );
    } catch (ex) {
      console.error('updateEinvoice unexpected error', ex);
      this.toastr.error('Failed to prepare eInvoice update request');
      return of({ status: 0, message: 'Unexpected error', response: null } as any);
    }
  }

  /**
   * verifyTin
   * - Accepts either a VerifyTin object: { idType, idValue, tin }
   *   OR a plain tin string as first arg (matching current component usage).
   * - Optional `token` will be sent as Authorization: Bearer <token>.
   * - Returns Observable<ApiResp>-like response so callers can use resp.status / resp.message.
   */
  /**
   * verifyTin
   * - Accepts VerifyTin: { idType, idValue, tin }
   * - Optional token string will be sent as Authorization: Bearer <token>
   * - Returns Observable<boolean> (uses this.booleanResp if available)
   */
  verifyTin(verify: VerifyTin, token?: string | null): Observable<boolean> {
    try {
      if (!verify || !verify.tin) {
        return of(false);
      }

      const url = `/myportal/validate/tin/${encodeURIComponent(verify.tin)}`;

      // Build headers (keep same b2c header set) and append Authorization when token present
      const headersObj: Record<string, string> = {
        ...(this.B2C_HEADER as Record<string, string>),
      };

      if (token) {
        headersObj['authorization'] = `Bearer ${token}`;
      }

      const headers = new HttpHeaders(headersObj);

      // Build params
      let params = new HttpParams();
      if (verify.idType !== undefined && verify.idType !== null) {
        params = params.set('idType', String(verify.idType));
      }
      if (verify.idValue !== undefined && verify.idValue !== null) {
        params = params.set('idValue', String(verify.idValue));
      }

      // Call backend and map to boolean using booleanResp (fallback to truthy check)
      return this.httpClient.get(url, { headers, params }).pipe(
        map((resp: any) => {
          try {
            return (this as any).booleanResp ? (this as any).booleanResp(resp) : !!resp;
          } catch {
            return !!resp;
          }
        }),
        catchError((err) => {
          console.error('verifyTin error', err);
          try {
            this.toastr.error('Network error while validating TIN');
          } catch {
            // noop
          }
          return of(false);
        }),
      );
    } catch (ex) {
      console.error('verifyTin unexpected error', ex);
      try {
        this.toastr.error('Failed to validate TIN');
      } catch {
        // noop
      }
      return of(false);
    }
  }

  checkClaim(payload: {
    invoiceId: string;
    tin: string;
    submissionId: string;
  }): Observable<ApiResp> {
    return this.httpClient
      .post<ApiResp>(
        this.SUBMIT_B2C_CLAIM_ENDPOINT,
        payload,
        this.getHeaders(), // includes b2c_claim_portal: true
      )
      .pipe(
        map((resp: ApiResp) => {
          if (resp?.status) return resp;
          this.toastr.error(resp?.message ?? 'Unable to fetch claim status');
          return resp;
        }),
        catchError((err) => {
          console.error('checkClaim error', err);
          this.toastr.error('Network error while checking claim status');
          return of({
            status: 0,
            message: 'Network error',
            response: null,
          } as ApiResp);
        }),
      );
  }

  printEinvoicePdf(
    payload: {
      fieldCriteriaList: any[];
      sortCriteria?: { field: string; direction: 'ASC' | 'DESC' };
      pageNumber?: number;
      pageSize?: number;
    },
    opts: {
      tin: string;
      type: 'sales' | 'purchase';
      merge?: boolean;
    },
    token: string,
  ): Observable<ApiResp> {
    // 🔐 Headers
    const headers = new HttpHeaders({
      ...(this.B2C_HEADER as Record<string, string>),
      Authorization: `Bearer ${token}`,
    });

    // 🔗 Query params
    let params = new HttpParams()
      .set('tin', opts.tin)
      .set('type', opts.type)
      .set('merge', String(opts.merge ?? false));

    return this.httpClient
      .post<ApiResp>(this.PRINT_PDF_ENDPOINT, payload, { headers, params })
      .pipe(
        map((resp: ApiResp) => {
          if (resp?.status) return resp;
          this.toastr.error(resp?.message ?? 'Unable to generate PDF');
          return resp;
        }),
        catchError((err) => {
          console.error('printEinvoicePdf error', err);
          this.toastr.error('Network error while generating PDF');
          return of({
            status: 0,
            message: 'Network error',
            response: null,
          } as ApiResp);
        }),
      );
  }
}
